import type {
  AdminSettingsView,
  AdminUserLimits,
  AdminUserList,
  AdminUserSaved,
  BudgetStatus,
  GeocodeResult,
  SearchRequest,
  SearchResponse,
  UserUsage,
} from '../domain/types';

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly budget?: BudgetStatus,
    readonly retryAfterSeconds?: number,
    /** The 403 every route gives while a password change is outstanding. */
    readonly passwordChangeRequired?: boolean,
    /** Present on a 429, so being refused still updates the figures on screen. */
    readonly usage?: UserUsage,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function readError(response: Response): Promise<ApiError> {
  let payload: {
    error?: string;
    budget?: BudgetStatus;
    retryAfterSeconds?: number;
    passwordChangeRequired?: boolean;
    usage?: UserUsage;
  } = {};
  try {
    payload = (await response.json()) as typeof payload;
  } catch {
    // Non-JSON error (proxy, offline). Fall through to the status-based message.
  }
  return new ApiError(
    payload.error ?? `Request failed (${response.status})`,
    response.status,
    payload.budget,
    payload.retryAfterSeconds,
    payload.passwordChangeRequired,
    payload.usage,
  );
}

export interface SignedIn {
  user: string;
  /**
   * Answered by the login reply itself rather than waited for on `/api/session`.
   *
   * The session call cannot be relied on for this: it runs on the heels of the login
   * response and needs the cookie that response sets, and a browser that has not
   * committed it yet answers 401 - which reads exactly like "no change due" and let a
   * brand-new account past the gate. An older deployment omits the field, so an
   * absent one means "ask the session", not false.
   */
  mustChangePassword?: boolean;
}

export async function login(username: string, password: string): Promise<SignedIn> {
  const response = await fetch('/api/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username, password }),
  });
  if (!response.ok) throw await readError(response);
  return (await response.json()) as SignedIn;
}

export async function logout(): Promise<void> {
  await fetch('/api/logout', { method: 'POST' });
}

export interface Session {
  user: string;
  /** Whether to offer the dashboard. The middleware is what actually enforces it. */
  admin: boolean;
  /** Signed in, but nothing works until the password is replaced. */
  mustChangePassword: boolean;
  budget: BudgetStatus;
  /** This account's own daily and monthly allowances. */
  usage: UserUsage;
  /** Deployment-wide ceiling on how many results the list will show. */
  maxResults: number;
}

/**
 * Replace your own password. Clears `mustChangePassword`.
 *
 * The current one is required even mid-forced-change: the person holding a generated
 * password is not necessarily the account's owner.
 */
export async function changePassword(
  currentPassword: string,
  newPassword: string,
): Promise<void> {
  const response = await fetch('/api/password', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ currentPassword, newPassword }),
  });
  if (!response.ok) throw await readError(response);
}

/** Resolves to null when signed out, rather than throwing on the expected 401. */
export async function getSession(): Promise<Session | null> {
  const response = await fetch('/api/session');
  if (response.status === 401) return null;
  if (!response.ok) throw await readError(response);
  return (await response.json()) as Session;
}

export async function search(request: SearchRequest): Promise<SearchResponse> {
  const response = await fetch('/api/search', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request),
  });
  if (!response.ok) throw await readError(response);
  return (await response.json()) as SearchResponse;
}

export async function geocode(query: string): Promise<GeocodeResult[]> {
  const response = await fetch(`/api/geocode?q=${encodeURIComponent(query)}`);
  if (!response.ok) throw await readError(response);
  const payload = (await response.json()) as { results: GeocodeResult[] };
  return payload.results;
}

// ---------------------------------------------------------------- admin

/**
 * The user dashboard's client.
 *
 * Every one of these is behind `admin` on the account, checked in
 * `functions/_middleware.ts` for the whole `/api/admin/` prefix - so a 403 here is a
 * real answer, not a bug, and the page renders it rather than retrying.
 */
const ADMIN_USERS = '/api/admin/users';

const adminUrl = (username: string) =>
  `${ADMIN_USERS}/${encodeURIComponent(username)}`;

export async function listAdminUsers(): Promise<AdminUserList> {
  const response = await fetch(ADMIN_USERS);
  if (!response.ok) throw await readError(response);
  return (await response.json()) as AdminUserList;
}

export interface NewAdminUser {
  username: string;
  /** Omitted means "generate one", which comes back in the reply exactly once. */
  password?: string;
  admin?: boolean;
  /** Defaults to true server-side; send false only for a deliberately shared login. */
  requirePasswordChange?: boolean;
  limits?: AdminUserLimits;
}

export async function createAdminUser(input: NewAdminUser): Promise<AdminUserSaved> {
  const response = await fetch(ADMIN_USERS, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (!response.ok) throw await readError(response);
  return (await response.json()) as AdminUserSaved;
}

/**
 * A patch: an absent key is left alone.
 *
 * `limits` carries a third state - a field set to `null` clears the override so the
 * account goes back to inheriting the deployment default. Without it there would be no
 * way back from a number once one had been set.
 */
export interface AdminUserPatch {
  admin?: boolean;
  disabled?: boolean;
  limits?: Record<string, number | null> | null;
  password?: string;
  resetPassword?: boolean;
  /** Only meaningful alongside a password write. Defaults to true server-side. */
  requirePasswordChange?: boolean;
}

export async function updateAdminUser(
  username: string,
  patch: AdminUserPatch,
): Promise<AdminUserSaved> {
  const response = await fetch(adminUrl(username), {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  });
  if (!response.ok) throw await readError(response);
  return (await response.json()) as AdminUserSaved;
}

export async function deleteAdminUser(username: string): Promise<void> {
  const response = await fetch(adminUrl(username), { method: 'DELETE' });
  if (!response.ok) throw await readError(response);
}

const ADMIN_SETTINGS = '/api/admin/settings';

export async function getAdminSettings(): Promise<AdminSettingsView> {
  const response = await fetch(ADMIN_SETTINGS);
  if (!response.ok) throw await readError(response);
  return (await response.json()) as AdminSettingsView;
}

/**
 * A patch, with the same three states as the per-user limits: an absent field is left
 * alone, `null` clears it back to the deployment default, a number sets it.
 */
export type AdminSettingsPatch = Record<string, number | null>;

export async function updateAdminSettings(
  patch: AdminSettingsPatch,
): Promise<AdminSettingsView> {
  const response = await fetch(ADMIN_SETTINGS, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  });
  if (!response.ok) throw await readError(response);
  return (await response.json()) as AdminSettingsView;
}
